import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Tv } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { TIMEZONES } from "@/lib/format";
import type { Group, Playlist, Screen } from "@/lib/types";
import { PlaylistSelect } from "@/components/app/ConfigEditor";

const NONE = "__none__";

export function PairingDialog({ open, onOpenChange, onPaired }: { open: boolean; onOpenChange: (o: boolean) => void; onPaired?: (s: Screen) => void }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"code" | "details">("code");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [groups, setGroups] = useState<Group[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [form, setForm] = useState({ name: "", group_id: NONE, use_group_config: true, timezone: "America/Toronto", default_playlist_id: null as string | null });

  useEffect(() => {
    if (!open) return;
    setCode(""); setStep("code"); setError("");
    setForm({ name: "", group_id: NONE, use_group_config: true, timezone: "America/Toronto", default_playlist_id: null });
    api.get<Group[]>("/groups").then((r) => setGroups(r.data)).catch(() => null);
    api.get<Playlist[]>("/playlists").then((r) => setPlaylists(r.data)).catch(() => null);
  }, [open]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  const lookup = () => run(async () => { await api.post("/pairing/lookup", { code }); setStep("details"); });
  const approve = () => run(async () => {
    const group_id = form.group_id === NONE ? null : form.group_id;
    const { data } = await api.post<Screen>("/pairing/approve", { code, name: form.name, timezone: form.timezone, group_id, use_group_config: !!group_id && form.use_group_config, default_playlist_id: form.default_playlist_id });
    toast.success(`Écran « ${data.name} » jumelé avec succès`);
    onOpenChange(false);
    onPaired?.(data);
    navigate(`/ecrans/${data.id}`);
  });
  const reject = () => run(async () => { await api.post("/pairing/reject", { code }); toast("Demande de jumelage refusée"); onOpenChange(false); });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pairing-dialog" className="border-slate-800 bg-slate-900 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl"><Tv className="h-5 w-5 text-cyan-400" />Appariement d'un écran Android TV</DialogTitle>
          <DialogDescription className="text-slate-400">{step === "code" ? "Saisissez le code à 6 chiffres affiché sur la TV. Il expire après 10 minutes." : `Code ${code} validé. Nommez l'écran et choisissez son contenu.`}</DialogDescription>
        </DialogHeader>

        {step === "code" ? (
          <div className="space-y-6 py-2">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={(v) => setCode(v.replace(/\D/g, ""))} data-testid="input-pairing-code" onComplete={() => null}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-14 w-12 border-slate-700 bg-slate-950 font-mono text-2xl text-cyan-300" />)}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {error && <p data-testid="pairing-error" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>}
            <Button data-testid="btn-pairing-verify" disabled={code.length !== 6 || busy} onClick={lookup} className="w-full bg-blue-600 text-white hover:bg-blue-500">
              <KeyRound className="mr-2 h-4 w-4" />{busy ? "Vérification…" : "Vérifier le code"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Nom de l'écran</Label><Input data-testid="input-screen-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Menu principal Resto Nord" className="border-slate-700 bg-slate-950/60" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Groupe d'attribution</Label>
                <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                  <SelectTrigger data-testid="select-screen-group" className="border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value={NONE}>Aucun groupe</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fuseau horaire</Label>
                <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                  <SelectTrigger data-testid="select-screen-timezone" className="border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-slate-900">{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.group_id !== NONE ? (
              <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3">
                <p className="text-sm text-slate-300">Utiliser la configuration du groupe</p>
                <Switch data-testid="switch-use-group-config" checked={form.use_group_config} onCheckedChange={(c) => setForm({ ...form, use_group_config: c })} />
              </div>
            ) : null}
            {(form.group_id === NONE || !form.use_group_config) && (
              <div className="space-y-2"><Label>Liste de lecture par défaut</Label><PlaylistSelect value={form.default_playlist_id} onChange={(v) => setForm({ ...form, default_playlist_id: v })} playlists={playlists} testId="select-screen-playlist" /></div>
            )}
            {error && <p data-testid="pairing-error" className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>}
            <div className="flex gap-2 pt-2">
              <Button data-testid="btn-reject-pairing" variant="outline" disabled={busy} onClick={reject} className="flex-1 border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700">Refuser</Button>
              <Button data-testid="btn-approve-pairing" disabled={busy || !form.name.trim()} onClick={approve} className="flex-1 bg-blue-600 text-white hover:bg-blue-500">{busy ? "Jumelage…" : "Approuver l'écran"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
